/*
*   Copyright 2026 Dynatrace LLC
*
*   Licensed under the Apache License, Version 2.0 (the "License");
*   you may not use this file except in compliance with the License.
*   You may obtain a copy of the License at
*
*   http://www.apache.org/licenses/LICENSE-2.0
*
*   Unless required by applicable law or agreed to in writing, software
*   distributed under the License is distributed on an "AS IS" BASIS,
*   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
*   See the License for the specific language governing permissions and
*   limitations under the License.
*/

// DO NOT EDIT, this is an Auto-generated file from ./templates/typescript_constants.ts.j2

/**
* Artifact Version
*/
export const ARTIFACT_VERSION = "0.36.4.20260115.164246";

// -----------------------------------------------------------------------------

/**
* Prefix for 'aws.lambda'
*/
export const AWS_LAMBDA_PREFIX = "aws.lambda";

/**
* Prefix for 'code'
*/
export const CODE_PREFIX = "code";

/**
* Prefix for 'db'
*/
export const DB_PREFIX = "db";

/**
* Prefix for 'db.mssql'
*/
export const DB_MSSQL_PREFIX = "db.mssql";

/**
* Prefix for 'db.cassandra'
*/
export const DB_CASSANDRA_PREFIX = "db.cassandra";

/**
* Prefix for 'db.hbase'
*/
export const DB_HBASE_PREFIX = "db.hbase";

/**
* Prefix for 'db.redis'
*/
export const DB_REDIS_PREFIX = "db.redis";

/**
* Prefix for 'db.mongodb'
*/
export const DB_MONGODB_PREFIX = "db.mongodb";

/**
* Prefix for 'dt.ctg'
*/
export const DT_CTG_PREFIX = "dt.ctg";

/**
* Prefix for 'dt.db'
*/
export const DT_DB_PREFIX = "dt.db";

/**
* Prefix for 'dt.exception'
*/
export const DT_EXCEPTION_PREFIX = "dt.exception";

/**
* Prefix for 'dt.faas'
*/
export const DT_FAAS_PREFIX = "dt.faas";

/**
* Prefix for 'dt.code'
*/
export const DT_CODE_PREFIX = "dt.code";

/**
* Prefix for 'dt.stacktrace'
*/
export const DT_STACKTRACE_PREFIX = "dt.stacktrace";

/**
* Prefix for 'trace'
*/
export const TRACE_PREFIX = "trace";

/**
* Prefix for 'dt.http.server'
*/
export const DT_HTTP_SERVER_PREFIX = "dt.http";

/**
* Prefix for 'dt.ims'
*/
export const DT_IMS_PREFIX = "dt.ims";

/**
* Prefix for 'otel.library'
*/
export const OTEL_LIBRARY_PREFIX = "otel.library";

/**
* Prefix for 'dt.messaging'
*/
export const DT_MESSAGING_PREFIX = "dt.messaging";

/**
* Prefix for 'dt.parent'
*/
export const DT_PARENT_PREFIX = "dt.parent";

/**
* Prefix for 'dt.rum'
*/
export const DT_RUM_PREFIX = "dt.rum";

/**
* Prefix for 'dt.zosconnect'
*/
export const DT_ZOSCONNECT_PREFIX = "dt.zosconnect";

/**
* Prefix for 'faas_span'
*/
export const FAAS_SPAN_PREFIX = "faas";

/**
* Prefix for 'faas_span.datasource'
*/
export const FAAS_SPAN_DATASOURCE_PREFIX = "faas.document";

/**
* Prefix for 'network'
*/
export const NETWORK_PREFIX = "net";

/**
* Prefix for 'identity'
*/
export const IDENTITY_PREFIX = "enduser";

/**
* Prefix for 'http'
*/
export const HTTP_PREFIX = "http";

/**
* Prefix for 'aws'
*/
export const AWS_PREFIX = "aws";


/**
* Prefix for 'dynamodb.shared'
*/
export const DYNAMODB_SHARED_PREFIX = "aws.dynamodb";

/**
* Prefix for 'messaging'
*/
export const MESSAGING_PREFIX = "messaging";

/**
* Prefix for 'rpc'
*/
export const RPC_PREFIX = "rpc";

/**
* Prefix for 'rpc.grpc'
*/
export const RPC_GRPC_PREFIX = "rpc.grpc";

/**
* Prefix for 'rpc.jsonrpc'
*/
export const RPC_JSONRPC_PREFIX = "rpc.jsonrpc";

/**
* Prefix for 'rpc.message'
*/
export const RPC_MESSAGE_PREFIX = "message";


// -----------------------------------------------------------------------------

/**
* The full invoked ARN as provided on the `Context` passed to the function (`Lambda-Runtime-Invoked-Function-Arn` header on the `/runtime/invocation/next` applicable).
* This attribute expects a value of type string.
*
* Note: This may be different from `faas.id` if an alias is involved.
*/
export const AWS_LAMBDA_INVOKED_ARN = "aws.lambda.invoked_arn";

/**
* The method or function name, or equivalent (usually rightmost part of the code unit's name).
* This attribute expects a value of type string.
*/
export const CODE_FUNCTION = "code.function";

/**
* The "namespace" within which `code.function` is defined. Usually the qualified class or module name, such that `code.namespace` + some separator + `code.function` form a unique identifier for the code unit.
* This attribute expects a value of type string.
*/
export const CODE_NAMESPACE = "code.namespace";

/**
* The source code file name that identifies the code unit as uniquely as possible (preferably an absolute file path).
* This attribute expects a value of type string.
*/
export const CODE_FILEPATH = "code.filepath";

/**
* The line number in `code.filepath` best representing the operation. It SHOULD point within the code unit named in code.function.
* This attribute expects a value of type int.
*/
export const CODE_LINENO = "code.lineno";

/**
* An identifier for the database management system (DBMS) product being used. See below for a list of well-known identifiers.
* This attribute expects a value of type string from the enumeration DbSystemValues.
*/
export const DB_SYSTEM = "db.system";

/**
* The connection string used to connect to the database.
* This attribute expects a value of type string.
*
* Note: It is recommended to remove embedded credentials.
*/
export const DB_CONNECTION_STRING = "db.connection_string";

/**
* Username for accessing the database.
* This attribute expects a value of type string.
*/
export const DB_USER = "db.user";

/**
* The fully-qualified class name of the [Java Database Connectivity (JDBC)](https://docs.oracle.com/javase/8/docs/technotes/guides/jdbc/) driver used to connect.
* This attribute expects a value of type string.
*/
export const DB_JDBC_DRIVER_CLASSNAME = "db.jdbc.driver_classname";

/**
* If no tech-specific attribute is defined, this attribute is used to report the name of the database being accessed. For commands that switch the database, this should be set to the target database (even if the command fails).
* This attribute expects a value of type string.
*
* Note: In some SQL databases, the database name to be used is called "schema name".
*/
export const DB_NAME = "db.name";

/**
* The database statement being executed.
* This attribute expects a value of type string.
*
* Note: The value may be sanitized to exclude sensitive information.
*/
export const DB_STATEMENT = "db.statement";

/**
* The name of the operation being executed, e.g. the [MongoDB command name](https://docs.mongodb.com/manual/reference/command/#database-operations) such as `findAndModify`.
* This attribute expects a value of type string.
*
* Note: While it would semantically make sense to set this, e.g., to a SQL keyword like `SELECT` or `INSERT`, it is not recommended to attempt any client-side parsing of `db.statement` just to get this property (the back end can do that if required).
*/
export const DB_OPERATION = "db.operation";

/**
* Remote hostname or similar, see note below.
* This attribute expects a value of type string.
*/
export const NET_PEER_NAME = "net.peer.name";

/**
* Remote address of the peer (dotted decimal for IPv4 or [RFC5952](https://rfc-editor.org/rfc/rfc5952) for IPv6).
* This attribute expects a value of type string.
*/
export const NET_PEER_IP = "net.peer.ip";

/**
* Remote port number.
* This attribute expects a value of type int.
*/
export const NET_PEER_PORT = "net.peer.port";

/**
* Transport protocol used. See note below.
* This attribute expects a value of type string from the enumeration NetTransportValues.
*/
export const NET_TRANSPORT = "net.transport";

/**
* The Microsoft SQL Server [instance name](https://docs.microsoft.com/en-us/sql/connect/jdbc/building-the-connection-url?view=sql-server-ver15) connecting to. This name is used to determine the port of a named instance.
* This attribute expects a value of type string.
*
* Note: If setting a `db.mssql.instance_name`, `net.peer.port` is no longer required (but still recommended if non-standard).
*/
export const DB_MSSQL_INSTANCE_NAME = "db.mssql.instance_name";

/**
* The name of the keyspace being accessed. To be used instead of the generic `db.name` attribute.
* This attribute expects a value of type string.
*/
export const DB_CASSANDRA_KEYSPACE = "db.cassandra.keyspace";

/**
* The [HBase namespace](https://hbase.apache.org/book.html#_namespace) being accessed. To be used instead of the generic `db.name` attribute.
* This attribute expects a value of type string.
*/
export const DB_HBASE_NAMESPACE = "db.hbase.namespace";

/**
* The index of the database being accessed as used in the [`SELECT` command](https://redis.io/commands/select), provided as an integer. To be used instead of the generic `db.name` attribute.
* This attribute expects a value of type int.
*/
export const DB_REDIS_DATABASE_INDEX = "db.redis.database_index";

/**
* The collection being accessed within the database stated in `db.name`.
* This attribute expects a value of type string.
*/
export const DB_MONGODB_COLLECTION = "db.mongodb.collection";

/**
* URL of the gateway.
* This attribute expects a value of type string.
*/
export const DT_CTG_GATEWAYURL = "dt.ctg.gatewayurl";

/**
* Type of the CTG GatewayRequest.
* This attribute expects a value of type string from the enumeration DtCtgRequesttypeValues.
*/
export const DT_CTG_REQUESTTYPE = "dt.ctg.requesttype";

/**
* Integer representing the specific calltype of the CTG GatewayRequest.
* This attribute expects a value of type int from the enumeration DtCtgCalltypeValues.
*/
export const DT_CTG_CALLTYPE = "dt.ctg.calltype";

/**
* ID/name of the server.
* This attribute expects a value of type string.
*/
export const DT_CTG_SERVERID = "dt.ctg.serverid";

/**
* ID/name of the user.
* This attribute expects a value of type string.
*/
export const DT_CTG_USERID = "dt.ctg.userid";

/**
* ID of the transaction.
* This attribute expects a value of type string.
*/
export const DT_CTG_TRANSID = "dt.ctg.transid";

/**
* Name of the CICS program.
* This attribute expects a value of type string.
*/
export const DT_CTG_PROGRAM = "dt.ctg.program";

/**
* Length of the communication area.
* This attribute expects a value of type int.
*/
export const DT_CTG_COMMAREALENGTH = "dt.ctg.commarealength";

/**
* See "ExtendModes" section below.
* This attribute expects a value of type int.
*/
export const DT_CTG_EXTENDMODE = "dt.ctg.extendmode";

/**
* Name of the terminal resource.
* This attribute expects a value of type string.
*/
export const DT_CTG_TERMID = "dt.ctg.termid";

/**
* CTG response code.
* This attribute expects a value of type int.
*/
export const DT_CTG_RC = "dt.ctg.rc";

/**
* The topology of the database in relation to the application performing database requests.
* This attribute expects a value of type string from the enumeration DtDbTopologyValues.
*/
export const DT_DB_TOPOLOGY = "dt.db.topology";

/**
* How a query is executed, as a query or update. Should not parse the query but should be determined by which method was called to execute the query. For example in JDBC terms it would be "executeQuery" -> query and all other "execute*" methods -> update.
* This attribute expects a value of type string from the enumeration DtDbExecutionTypeValues.
*/
export const DT_DB_EXECUTION_TYPE = "dt.db.execution_type";

/**
* The exception types of a caused-by chain, represented by their fully-qualified type names encoded as a single string (see [Encoding of Exception Data](https://bitbucket.lab.dynatrace.org/projects/ODIN/repos/odin-spec/browse/spec/semantic_conventions/exception_conventions.md#encoding-of-exception-data)).
* This attribute expects a value of type string.
*/
export const DT_EXCEPTION_TYPES = "dt.exception.types";

/**
* Messages providing details about the exceptions of a caused-by chain encoded as a single string (see [Encoding of Exception Data](https://bitbucket.lab.dynatrace.org/projects/ODIN/repos/odin-spec/browse/spec/semantic_conventions/exception_conventions.md#encoding-of-exception-data)).
* This attribute expects a value of type string.
*/
export const DT_EXCEPTION_MESSAGES = "dt.exception.messages";

/**
* Stack traces for all exceptions in a caused-by chain, serialized into a single string (see [Encoding of Exception Data](https://bitbucket.lab.dynatrace.org/projects/ODIN/repos/odin-spec/browse/spec/semantic_conventions/exception_conventions.md#encoding-of-exception-data)).
* This attribute expects a value of type string.
*/
export const DT_EXCEPTION_SERIALIZED_STACKTRACES = "dt.exception.serialized_stacktraces";

/**
* Type of the trigger on which the function is executed.
* This attribute expects a value of type string from the enumeration FaasTriggerValues.
*/
export const FAAS_TRIGGER = "faas.trigger";

/**
* The execution ID of the current function execution.
* This attribute expects a value of type string.
*/
export const FAAS_EXECUTION = "faas.execution";

/**
* The `X-Amzn-Trace-Id` HTTP response header for [AWS X-Ray](https://docs.aws.amazon.com/xray/latest/devguide/aws-xray.html) tracing.
* This attribute expects a value of type string.
*/
export const DT_FAAS_AWS_X_AMZN_TRACE_ID = "dt.faas.aws.x_amzn_trace_id";

/**
* The AWS `X-Amzn-RequestId` HTTP response header.
* This attribute expects a value of type string.
*
* Note: The different notation of the `RequestId` part (missing `-` character) compared to `X-Amzn-Trace-Id` is intentional.
*/
export const DT_FAAS_AWS_X_AMZN_REQUEST_ID = "dt.faas.aws.x_amzn_request_id";

/**
* The method or function name, or equivalent (usually rightmost part of the code unit's name).
* This attribute expects a value of type string.
*
* @deprecated Use `code.function` instead.
*/
export const DT_CODE_FUNC = "dt.code.func";

/**
* The "namespace" within which `dt.code.func` is defined. Usually the qualified class or module name, such that `dt.code.ns` + some separator + `dt.code.func` form a unique identifier for the code unit.
* This attribute expects a value of type string.
*
* @deprecated Use `code.namespace` instead.
*/
export const DT_CODE_NS = "dt.code.ns";

/**
* The source code file name that identifies the code unit as uniquely as possible (preferably an absolute file path).
* This attribute expects a value of type string.
*
* @deprecated Use `code.filepath` instead.
*/
export const DT_CODE_FILEPATH = "dt.code.filepath";

/**
* The line number in `dt.code.filepath` best representing the operation. It SHOULD point within the code unit named in `dt.code.func`.
* This attribute expects a value of type int.
*
* @deprecated Use `code.lineno` instead.
*/
export const DT_CODE_LINENO = "dt.code.lineno";

/**
* Full stacktrace of call to Span.Start (possibly including call to span processor's OnStart).
* This attribute expects a value of type string.
*/
export const DT_STACKTRACE_ONSTART = "dt.stacktrace.onstart";

/**
* Full stacktrace of call to Span.End (possibly including call to span processor's OnEnd).
* This attribute expects a value of type string.
*/
export const DT_STACKTRACE_ONEND = "dt.stacktrace.onend";

/**
* Explains why this trace was captured, multiple reasons can apply simultaneously. Trace capture reasons description can be found [here](https://bitbucket.lab.dynatrace.org/projects/DEUS/repos/semantic-dictionary/browse/doc/fields/trace.md).
* This attribute expects a value of type string[].
*/
export const TRACE_CAPTURE_REASONS = "trace.capture.reasons";

/**
* HTTP request method.
* This attribute expects a value of type string.
*/
export const HTTP_METHOD = "http.method";

/**
* Full HTTP request URL in the form `scheme://host[:port]/path?query[#fragment]`. Usually the fragment is not transmitted over HTTP, but if it is known, it should be included nevertheless.
* This attribute expects a value of type string.
*/
export const HTTP_URL = "http.url";

/**
* The full request target as passed in a HTTP request line or equivalent.
* This attribute expects a value of type string.
*/
export const HTTP_TARGET = "http.target";

/**
* The value of the [HTTP host header](https://rfc-editor.org/rfc/rfc7230#section-5.4). When the header is empty or not present, this attribute should be the same.
* This attribute expects a value of type string.
*/
export const HTTP_HOST = "http.host";

/**
* The URI scheme identifying the used protocol.
* This attribute expects a value of type string.
*/
export const HTTP_SCHEME = "http.scheme";

/**
* [HTTP response status code](https://rfc-editor.org/rfc/rfc7231#section-6).
* This attribute expects a value of type int.
*/
export const HTTP_STATUS_CODE = "http.status_code";

/**
* [HTTP reason phrase](https://rfc-editor.org/rfc/rfc7230#section-3.1.2).
* This attribute expects a value of type string.
*/
export const HTTP_STATUS_TEXT = "http.status_text";

/**
* Kind of HTTP protocol used.
* This attribute expects a value of type string from the enumeration HttpFlavorValues.
*
* Note: If `net.transport` is not specified, it can be assumed to be `IP.TCP` except if `http.flavor` is `QUIC`, in which case `IP.UDP` is assumed.
*/
export const HTTP_FLAVOR = "http.flavor";

/**
* Value of the [HTTP User-Agent](https://rfc-editor.org/rfc/rfc7231#section-5.5.3) header sent by the client.
* This attribute expects a value of type string.
*/
export const HTTP_USER_AGENT = "http.user_agent";

/**
* The size of the request payload body in bytes. This is the number of bytes transferred excluding headers and is often, but not always, present as the [Content-Length](https://rfc-editor.org/rfc/rfc7230#section-3.3.2) header. For requests using transport encoding, this should be the compressed size.
* This attribute expects a value of type int.
*/
export const HTTP_REQUEST_CONTENT_LENGTH = "http.request_content_length";

/**
* The size of the uncompressed request payload body after transport decoding. Not set if transport encoding not used.
* This attribute expects a value of type int.
*/
export const HTTP_REQUEST_CONTENT_LENGTH_UNCOMPRESSED = "http.request_content_length_uncompressed";

/**
* The size of the response payload body in bytes. This is the number of bytes transferred excluding headers and is often, but not always, present as the [Content-Length](https://rfc-editor.org/rfc/rfc7230#section-3.3.2) header. For requests using transport encoding, this should be the compressed size.
* This attribute expects a value of type int.
*/
export const HTTP_RESPONSE_CONTENT_LENGTH = "http.response_content_length";

/**
* The size of the uncompressed response payload body after transport decoding. Not set if transport encoding not used.
* This attribute expects a value of type int.
*/
export const HTTP_RESPONSE_CONTENT_LENGTH_UNCOMPRESSED = "http.response_content_length_uncompressed";

/**
* Like `net.peer.ip` but for the host IP. Useful in case of a multi-IP host.
* This attribute expects a value of type string.
*/
export const NET_HOST_IP = "net.host.ip";

/**
* Like `net.peer.port` but for the host port.
* This attribute expects a value of type int.
*/
export const NET_HOST_PORT = "net.host.port";

/**
* Local hostname or similar, see note below.
* This attribute expects a value of type string.
*/
export const NET_HOST_NAME = "net.host.name";

/**
* The primary server name of the matched virtual host. This should be obtained via configuration. If no such configuration can be obtained, this attribute MUST NOT be set ( `net.host.name` should be used instead).
* This attribute expects a value of type string.
*
* Note: http.url is usually not readily available on the server side but would have to be assembled in a cumbersome and sometimes lossy process from other information (see e.g. open-telemetry/opentelemetry-python/pull/148). It is thus preferred to supply the raw data that is available.
*/
export const HTTP_SERVER_NAME = "http.server_name";

/**
* The matched route (path template).
* This attribute expects a value of type string.
*/
export const HTTP_ROUTE = "http.route";

/**
* The IP address of the original client behind all proxies, if known (e.g. from [X-Forwarded-For](https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Forwarded-For)).
* This attribute expects a value of type string.
*
* Note: This is not necessarily the same as `net.peer.ip`, which would identify the network-level peer, which may be a proxy.
*/
export const HTTP_CLIENT_IP = "http.client_ip";

/**
* Tech-dependent, eg. servlet context.
* This attribute expects a value of type string.
*/
export const DT_HTTP_APPLICATION_ID = "dt.http.application_id";

/**
* The path prefix of the URL that identifies this HTTP application. If multiple roots exist, the one that was matched for this request should be used.
* This attribute expects a value of type string.
*
* Note: See [OTel definition](https://github.com/open-telemetry/opentelemetry-specification/blob/v0.6.0/specification/trace/semantic_conventions/http.md#http-server-definitions).
*/
export const DT_HTTP_CONTEXT_ROOT = "dt.http.context_root";

/**
* `Referer` header.
* This attribute expects a value of type string.
*/
export const DT_HTTP_REQUEST_HEADER_REFERER = "dt.http.request.header.referer";

/**
* `X-Dynatrace-Test` header.
* This attribute expects a value of type string.
*/
export const DT_HTTP_REQUEST_HEADER_X_DYNATRACE_TEST = "dt.http.request.header.x-dynatrace-test";

/**
* `X-Dynatrace-Tenant` header.
* This attribute expects a value of type string.
*/
export const DT_HTTP_REQUEST_HEADER_X_DYNATRACE_TENANT = "dt.http.request.header.x-dynatrace-tenant";

/**
* `Forwarded` header.
* This attribute expects a value of type string.
*/
export const DT_HTTP_REQUEST_HEADER_FORWARDED = "dt.http.request.header.forwarded";

/**
* `X-Forwarded-For` header (only required if `dt.http.request.header.forwarded` isn't provided).
* This attribute expects a value of type string.
*/
export const DT_HTTP_REQUEST_HEADER_X_FORWARDED_FOR = "dt.http.request.header.x-forwarded-for";

/**
* Set to true for an interaction with IMS SOAP Gateway.
* This attribute expects a value of type boolean.
*
* Note: This value is normally either unset or `true`.
*/
export const DT_IMS_IS_IMS = "dt.ims.is_ims";

/**
* Contains the instrumentation library name.
* This attribute expects a value of type string.
*/
export const OTEL_LIBRARY_NAME = "otel.library.name";

/**
* Contains the instrumentation library version.
* This attribute expects a value of type string.
*/
export const OTEL_LIBRARY_VERSION = "otel.library.version";

/**
* A string identifying the messaging system.
* This attribute expects a value of type string.
*/
export const MESSAGING_SYSTEM = "messaging.system";

/**
* The message destination name. This might be equal to the span name but is required nevertheless.
* This attribute expects a value of type string.
*/
export const MESSAGING_DESTINATION = "messaging.destination";

/**
* The kind of message destination.
* This attribute expects a value of type string from the enumeration MessagingDestinationKindValues.
*/
export const MESSAGING_DESTINATION_KIND = "messaging.destination_kind";

/**
* A boolean that is true if the message destination is temporary.
* This attribute expects a value of type boolean.
*/
export const MESSAGING_TEMP_DESTINATION = "messaging.temp_destination";

/**
* The name of the transport protocol.
* This attribute expects a value of type string.
*/
export const MESSAGING_PROTOCOL = "messaging.protocol";

/**
* The version of the transport protocol.
* This attribute expects a value of type string.
*/
export const MESSAGING_PROTOCOL_VERSION = "messaging.protocol_version";

/**
* Connection string.
* This attribute expects a value of type string.
*/
export const MESSAGING_URL = "messaging.url";

/**
* A value used by the messaging system as an identifier for the message, represented as a string.
* This attribute expects a value of type string.
*/
export const MESSAGING_MESSAGE_ID = "messaging.message_id";

/**
* A value identifying the conversation to which the message belongs, represented as a string. Sometimes called "Correlation ID".
* This attribute expects a value of type string.
*/
export const MESSAGING_CONVERSATION_ID = "messaging.conversation_id";

/**
* The (uncompressed) size of the message payload in bytes. Also use this attribute if it is unknown whether the compressed or uncompressed payload size is reported.
* This attribute expects a value of type int.
*/
export const MESSAGING_MESSAGE_PAYLOAD_SIZE_BYTES = "messaging.message_payload_size_bytes";

/**
* The compressed size of the message payload in bytes.
* This attribute expects a value of type int.
*/
export const MESSAGING_MESSAGE_PAYLOAD_COMPRESSED_SIZE_BYTES = "messaging.message_payload_compressed_size_bytes";

/**
* Name of IBM's queue manager.
* This attribute expects a value of type string.
*
* Note: Only for IBM MQ spans.
*/
export const DT_MESSAGING_IBM_QUEUEMANAGER_NAME = "dt.messaging.ibm.queuemanager.name";

/**
* The type of content of the jms message.
* This attribute expects a value of type string from the enumeration DtMessagingJmsMessageTypeValues.
*/
export const DT_MESSAGING_JMS_MESSAGE_TYPE = "dt.messaging.jms.message_type";

/**
* Number of messages being sent/received/processed at once.
* This attribute expects a value of type int.
*
* Note: This batch size attribute is kind of a workaround for situations where we cannot better distinguish the individual messages.
*/
export const DT_MESSAGING_BATCH_SIZE = "dt.messaging.batch_size";

/**
* MUST be set to `true` if this is a link that would have been the parent but was suppressed by the OpenTelemetry integration.
* This attribute expects a value of type boolean.
*/
export const DT_PARENT_IS_SUPPRESSED_PRIMARY = "dt.parent.is_suppressed_primary";

/**
* Value of x-dtc header.
* This attribute expects a value of type string.
*/
export const DT_RUM_DTC = "dt.rum.dtc";

/**
* Monitored entity id of configured application.
* This attribute expects a value of type string.
*/
export const DT_RUM_APP_ME_ID = "dt.rum.app_me_id";

/**
* Name of the header in `dt.http.request.headers` to parse clientip from.
* This attribute expects a value of type string.
*/
export const DT_RUM_CLIENTIP_HEADER_NAME = "dt.rum.clientip_header_name";

/**
* The name of the z/OS application program called by the request.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_PROGRAM = "dt.zosconnect.program";

/**
* The z/OS Connect request ID.
* This attribute expects a value of type int.
*/
export const DT_ZOSCONNECT_REQUEST_ID = "dt.zosconnect.request_id";

/**
* The service provider name.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_SERVICE_PROVIDER_NAME = "dt.zosconnect.service_provider_name";

/**
* The system of record reference.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_SOR_REFERENCE = "dt.zosconnect.sor_reference";

/**
* The system of record identifier. The format differs depending on the SOR type.
* This attribute expects a value of type string.
*
* Note: <https://www.ibm.com/docs/en/zos-connect/zosconnect/3.0?topic=spi-data#SOR_IDENTIFIER>.
*/
export const DT_ZOSCONNECT_SOR_IDENTIFIER = "dt.zosconnect.sor_identifier";

/**
* Identifier for the resource invoked on the system of record. The format differs depending on the SOR type.
* This attribute expects a value of type string.
*
* Note: <https://www.ibm.com/docs/en/zos-connect/zosconnect/3.0?topic=spi-data#SOR_RESOURCE>.
*/
export const DT_ZOSCONNECT_SOR_RESOURCE = "dt.zosconnect.sor_resource";

/**
* The system of record type.
* This attribute expects a value of type string from the enumeration DtZosconnectSorTypeValues.
*/
export const DT_ZOSCONNECT_SOR_TYPE = "dt.zosconnect.sor_type";

/**
* The length of the request payload in bytes.
* This attribute expects a value of type int.
*/
export const DT_ZOSCONNECT_INPUT_PAYLOAD_LENGTH = "dt.zosconnect.input_payload_length";

/**
* The length of the response payload in bytes.
* This attribute expects a value of type int.
*/
export const DT_ZOSCONNECT_OUTPUT_PAYLOAD_LENGTH = "dt.zosconnect.output_payload_length";

/**
* The z/OS Connect API name.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_API_NAME = "dt.zosconnect.api_name";

/**
* The z/OS Connect service name.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_SERVICE_NAME = "dt.zosconnect.service_name";

/**
* The z/OS Connect API description.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_API_DESCRIPTION = "dt.zosconnect.api_description";

/**
* The z/OS Connect service description.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_SERVICE_DESCRIPTION = "dt.zosconnect.service_description";

/**
* The z/OS Connect API version.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_API_VERSION = "dt.zosconnect.api_version";

/**
* The z/OS Connect service version.
* This attribute expects a value of type string.
*/
export const DT_ZOSCONNECT_SERVICE_VERSION = "dt.zosconnect.service_version";

/**
* The type of the REST request.
* This attribute expects a value of type string from the enumeration DtZosconnectRequestTypeValues.
*
* Note: <https://www.ibm.com/docs/en/zos-connect/zosconnect/3.0?topic=spi-datarequesttype>.
*/
export const DT_ZOSCONNECT_REQUEST_TYPE = "dt.zosconnect.request_type";

/**
* The name of the source on which the triggering operation was performed. For example, in Cloud Storage or S3 corresponds to the bucket name, and in Cosmos DB to the database name.
* This attribute expects a value of type string.
*/
export const FAAS_DOCUMENT_COLLECTION = "faas.document.collection";

/**
* Describes the type of the operation that was performed on the data.
* This attribute expects a value of type string from the enumeration FaasDocumentOperationValues.
*/
export const FAAS_DOCUMENT_OPERATION = "faas.document.operation";

/**
* A string containing the time when the data was accessed in the [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format expressed in [UTC](https://www.w3.org/TR/NOTE-datetime).
* This attribute expects a value of type string.
*/
export const FAAS_DOCUMENT_TIME = "faas.document.time";

/**
* The document name/table subjected to the operation. For example, in Cloud Storage or S3 is the name of the file, and in Cosmos DB the table name.
* This attribute expects a value of type string.
*/
export const FAAS_DOCUMENT_NAME = "faas.document.name";

/**
* A string containing the function invocation time in the [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format expressed in [UTC](https://www.w3.org/TR/NOTE-datetime).
* This attribute expects a value of type string.
*/
export const FAAS_TIME = "faas.time";

/**
* A string containing the schedule period as [Cron Expression](https://docs.oracle.com/cd/E12058_01/doc/doc.1014/e12030/cron_expressions.htm).
* This attribute expects a value of type string.
*/
export const FAAS_CRON = "faas.cron";

/**
* A boolean that is true if the serverless function is executed for the first time (aka cold-start).
* This attribute expects a value of type boolean.
*/
export const FAAS_COLDSTART = "faas.coldstart";

/**
* The name of the invoked function.
* This attribute expects a value of type string.
*
* Note: SHOULD be equal to the `faas.name` resource attribute of the invoked function.
*/
export const FAAS_INVOKED_NAME = "faas.invoked_name";

/**
* The cloud provider of the invoked function.
* This attribute expects a value of type string from the enumeration FaasInvokedProviderValues.
*
* Note: SHOULD be equal to the `cloud.provider` resource attribute of the invoked function.
*/
export const FAAS_INVOKED_PROVIDER = "faas.invoked_provider";

/**
* The cloud region of the invoked function.
* This attribute expects a value of type string.
*
* Note: SHOULD be equal to the `cloud.region` resource attribute of the invoked function.
*/
export const FAAS_INVOKED_REGION = "faas.invoked_region";

/**
* Username or client_id extracted from the access token or Authorization header in the inbound request from outside the system.
* This attribute expects a value of type string.
*/
export const ENDUSER_ID = "enduser.id";

/**
* Actual/assumed role the client is making the request under extracted from token or application security context.
* This attribute expects a value of type string.
*/
export const ENDUSER_ROLE = "enduser.role";

/**
* Scopes or granted authorities the client currently possesses extracted from token or application security context. The value would come from the scope associated with an OAuth 2.0 Access Token or an attribute value in a SAML 2.0 Assertion.
* This attribute expects a value of type string.
*/
export const ENDUSER_SCOPE = "enduser.scope";

/**
* The value `aws-api`.
* This attribute expects a value of type string from the enumeration RpcSystemValues.
*/
export const RPC_SYSTEM = "rpc.system";

/**
* The name of the service to which a request is made, as returned by the AWS SDK.
* This attribute expects a value of type string.
*
* Note: This is the logical name of the service from the RPC interface perspective, which can be different from the name of any implementing class. The `code.namespace` attribute may be used to store the latter (despite the attribute name, it may include a class name; e.g., class with method actually executing the call on the server side, RPC client stub class on the client side).
*/
export const RPC_SERVICE = "rpc.service";

/**
* The name of the operation corresponding to the request, as returned by the AWS SDK.
* This attribute expects a value of type string.
*
* Note: This is the logical name of the method from the RPC interface perspective, which can be different from the name of any implementing method/function. The `code.function` attribute may be used to store the latter (e.g., method actually executing the call on the server side, RPC client stub method on the client side).
*/
export const RPC_METHOD = "rpc.method";

/**
* The keys in the `RequestItems` object field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_TABLE_NAMES = "aws.dynamodb.table_names";

/**
* The JSON-serialized value of each item in the `ConsumedCapacity` response field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_CONSUMED_CAPACITY = "aws.dynamodb.consumed_capacity";

/**
* The JSON-serialized value of the `ItemCollectionMetrics` response field.
* This attribute expects a value of type string.
*/
export const AWS_DYNAMODB_ITEM_COLLECTION_METRICS = "aws.dynamodb.item_collection_metrics";

/**
* The value of the `ProvisionedThroughput.ReadCapacityUnits` request parameter.
* This attribute expects a value of type double.
*/
export const AWS_DYNAMODB_PROVISIONED_READ_CAPACITY = "aws.dynamodb.provisioned_read_capacity";

/**
* The value of the `ProvisionedThroughput.WriteCapacityUnits` request parameter.
* This attribute expects a value of type double.
*/
export const AWS_DYNAMODB_PROVISIONED_WRITE_CAPACITY = "aws.dynamodb.provisioned_write_capacity";

/**
* The value of the `ConsistentRead` request parameter.
* This attribute expects a value of type boolean.
*/
export const AWS_DYNAMODB_CONSISTENT_READ = "aws.dynamodb.consistent_read";

/**
* The value of the `ProjectionExpression` request parameter.
* This attribute expects a value of type string.
*/
export const AWS_DYNAMODB_PROJECTION = "aws.dynamodb.projection";

/**
* The value of the `Limit` request parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_LIMIT = "aws.dynamodb.limit";

/**
* The value of the `AttributesToGet` request parameter.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_ATTRIBUTES_TO_GET = "aws.dynamodb.attributes_to_get";

/**
* The value of the `IndexName` request parameter.
* This attribute expects a value of type string.
*/
export const AWS_DYNAMODB_INDEX_NAME = "aws.dynamodb.index_name";

/**
* The value of the `Select` request parameter.
* This attribute expects a value of type string.
*/
export const AWS_DYNAMODB_SELECT = "aws.dynamodb.select";

/**
* The JSON-serialized value of each item of the `GlobalSecondaryIndexes` request field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_GLOBAL_SECONDARY_INDEXES = "aws.dynamodb.global_secondary_indexes";

/**
* The JSON-serialized value of each item of the `LocalSecondaryIndexes` request field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_LOCAL_SECONDARY_INDEXES = "aws.dynamodb.local_secondary_indexes";

/**
* The value of the `ExclusiveStartTableName` request parameter.
* This attribute expects a value of type string.
*/
export const AWS_DYNAMODB_EXCLUSIVE_START_TABLE = "aws.dynamodb.exclusive_start_table";

/**
* The the number of items in the `TableNames` response parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_TABLE_COUNT = "aws.dynamodb.table_count";

/**
* The value of the `ScanIndexForward` request parameter.
* This attribute expects a value of type boolean.
*/
export const AWS_DYNAMODB_SCAN_FORWARD = "aws.dynamodb.scan_forward";

/**
* The value of the `Segment` request parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_SEGMENT = "aws.dynamodb.segment";

/**
* The value of the `TotalSegments` request parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_TOTAL_SEGMENTS = "aws.dynamodb.total_segments";

/**
* The value of the `Count` response parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_COUNT = "aws.dynamodb.count";

/**
* The value of the `ScannedCount` response parameter.
* This attribute expects a value of type int.
*/
export const AWS_DYNAMODB_SCANNED_COUNT = "aws.dynamodb.scanned_count";

/**
* The JSON-serialized value of each item in the `AttributeDefinitions` request field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_ATTRIBUTE_DEFINITIONS = "aws.dynamodb.attribute_definitions";

/**
* The JSON-serialized value of each item in the the `GlobalSecondaryIndexUpdates` request field.
* This attribute expects a value of type string[].
*/
export const AWS_DYNAMODB_GLOBAL_SECONDARY_INDEX_UPDATES = "aws.dynamodb.global_secondary_index_updates";

/**
* The cloud region of the invoked resource.
* This attribute expects a value of type string.
*
* Note: SHOULD be equal to the `cloud.region` resource attribute of the invoked resource.
*/
export const AWS_REGION = "aws.region";

/**
* A string identifying which part and kind of message consumption this span describes.
* This attribute expects a value of type string from the enumeration MessagingOperationValues.
*/
export const MESSAGING_OPERATION = "messaging.operation";

/**
* The [numeric status code](https://github.com/grpc/grpc/blob/v1.33.2/doc/statuscodes.md) of the gRPC request.
* This attribute expects a value of type int from the enumeration RpcGrpcStatusCodeValues.
*/
export const RPC_GRPC_STATUS_CODE = "rpc.grpc.status_code";

/**
* Protocol version as in `jsonrpc` property of request/response. Since JSON-RPC 1.0 does not specify this, the value can be omitted.
* This attribute expects a value of type string.
*/
export const RPC_JSONRPC_VERSION = "rpc.jsonrpc.version";

/**
* `id` property of request or response. Since protocol allows id to be int, string, `null` or missing (for notifications), value is expected to be cast to string for simplicity. Use empty string in case of `null` value. Omit entirely if this is a notification.
* This attribute expects a value of type string.
*/
export const RPC_JSONRPC_REQUEST_ID = "rpc.jsonrpc.request_id";

/**
* `error.code` property of response if it is an error response.
* This attribute expects a value of type int.
*/
export const RPC_JSONRPC_ERROR_CODE = "rpc.jsonrpc.error_code";

/**
* `error.message` property of response if it is an error response.
* This attribute expects a value of type string.
*/
export const RPC_JSONRPC_ERROR_MESSAGE = "rpc.jsonrpc.error_message";

/**
* Whether this is a received or sent message.
* This attribute expects a value of type string from the enumeration MessageTypeValues.
*/
export const MESSAGE_TYPE = "message.type";

/**
* MUST be calculated as two different counters starting from `1` one for sent messages and one for received message.
* This attribute expects a value of type int.
*
* Note: This way we guarantee that the values will be consistent between different implementations.
*/
export const MESSAGE_ID = "message.id";

/**
* Compressed size of the message in bytes.
* This attribute expects a value of type int.
*/
export const MESSAGE_COMPRESSED_SIZE = "message.compressed_size";

/**
* Uncompressed size of the message in bytes.
* This attribute expects a value of type int.
*/
export const MESSAGE_UNCOMPRESSED_SIZE = "message.uncompressed_size";


// Enum definitions
export enum DbSystemValues {
   OTHER_SQL = "other_sql",
   MSSQL = "mssql",
   MYSQL = "mysql",
   ORACLE = "oracle",
   DB2 = "db2",
   POSTGRESQL = "postgresql",
   REDSHIFT = "redshift",
   HIVE = "hive",
   CLOUDSCAPE = "cloudscape",
   HSQLSB = "hsqlsb",
   PROGRESS = "progress",
   MAXDB = "maxdb",
   HANADB = "hanadb",
   INGRES = "ingres",
   FIRSTSQL = "firstsql",
   EDB = "edb",
   CACHE = "cache",
   ADABAS = "adabas",
   FIREBIRD = "firebird",
   DERBY = "derby",
   FILEMAKER = "filemaker",
   INFORMIX = "informix",
   INSTANTDB = "instantdb",
   INTERBASE = "interbase",
   MARIADB = "mariadb",
   NETEZZA = "netezza",
   PERVASIVE = "pervasive",
   POINTBASE = "pointbase",
   SQLITE = "sqlite",
   SYBASE = "sybase",
   TERADATA = "teradata",
   VERTICA = "vertica",
   H2 = "h2",
   COLDFUSION = "coldfusion",
   CASSANDRA = "cassandra",
   HBASE = "hbase",
   MONGODB = "mongodb",
   REDIS = "redis",
   COUCHBASE = "couchbase",
   COUCHDB = "couchdb",
   COSMOSDB = "cosmosdb",
   DYNAMODB = "dynamodb",
   NEO4J = "neo4j",
}

export enum NetTransportValues {
   IP_TCP = "IP.TCP",
   IP_UDP = "IP.UDP",
   IP = "IP",
   UNIX = "Unix",
   PIPE = "pipe",
   INPROC = "inproc",
   OTHER = "other",
}

export enum DtCtgRequesttypeValues {
   BASE = "BASE",
   ECI = "ECI",
   EPI = "EPI",
   ESI = "ESI",
   XA = "XA",
   ADMIN = "ADMIN",
   AUTH = "AUTH",
}

export enum DtCtgCalltypeValues {
   ECI_UNKNOWN_CALL_TYPE = 0,
   ECI_SYNC = 1,
   ECI_ASYNC = 2,
   ECI_GET_REPLY = 3,
   ECI_GET_REPLY_WAIT = 4,
   ECI_GET_SPECIFIC_REPLY = 5,
   ECI_GET_SPECIFIC_REPLY_WAIT = 6,
   ECI_STATE_SYNC = 7,
   ECI_STATE_ASYNC = 8,
   CICS_ECI_LIST_SYSTEMS = 9,
   ECI_STATE_SYNC_JAVA = 10,
   ECI_STATE_ASYNC_JAVA = 11,
   ECI_SYNC_TPN = 12,
   ECI_ASYNC_TPN = 13,
}

export enum DtDbTopologyValues {
   NOT_SET = "not_set",
   SINGLE_SERVER = "single_server",
   EMBEDDED = "embedded",
   FAILOVER = "failover",
   LOAD_BALANCING = "load_balancing",
   LOCAL_IPC = "local_ipc",
   CLUSTER = "cluster",
}

export enum DtDbExecutionTypeValues {
   UPDATE = "update",
   QUERY = "query",
}

export enum FaasTriggerValues {
   DATASOURCE = "datasource",
   HTTP = "http",
   PUBSUB = "pubsub",
   TIMER = "timer",
   OTHER = "other",
}

export enum HttpFlavorValues {
   HTTP_1_0 = "1.0",
   HTTP_1_1 = "1.1",
   HTTP_2_0 = "2.0",
   SPDY = "SPDY",
   QUIC = "QUIC",
}

export enum MessagingDestinationKindValues {
   QUEUE = "queue",
   TOPIC = "topic",
}

export enum DtMessagingJmsMessageTypeValues {
   OTHER = "other",
   MAP = "map",
   OBJECT = "object",
   STREAM = "stream",
   BYTES = "bytes",
   TEXT = "text",
}

export enum DtZosconnectSorTypeValues {
   CICS = "CICS",
   IMS = "IMS",
   REST = "REST",
   WOLA = "WOLA",
   MQ = "MQ",
}

export enum DtZosconnectRequestTypeValues {
   API = "API",
   SERVICE = "SERVICE",
   ADMIN = "ADMIN",
   UNKNOWN = "UNKNOWN",
}

export enum FaasDocumentOperationValues {
   INSERT = "insert",
   EDIT = "edit",
   DELETE = "delete",
}

export enum FaasInvokedProviderValues {
   AWS = "aws",
   AZURE = "azure",
   GCP = "gcp",
}

export enum RpcSystemValues {
   GRPC = "grpc",
   JAVA_RMI = "java_rmi",
   DOTNET_WCF = "dotnet_wcf",
   APACHE_DUBBO = "apache_dubbo",
}

export enum MessagingOperationValues {
   RECEIVE = "receive",
   PROCESS = "process",
}

export enum RpcGrpcStatusCodeValues {
   OK = 0,
   CANCELLED = 1,
   UNKNOWN = 2,
   INVALID_ARGUMENT = 3,
   DEADLINE_EXCEEDED = 4,
   NOT_FOUND = 5,
   ALREADY_EXISTS = 6,
   PERMISSION_DENIED = 7,
   RESOURCE_EXHAUSTED = 8,
   FAILED_PRECONDITION = 9,
   ABORTED = 10,
   OUT_OF_RANGE = 11,
   UNIMPLEMENTED = 12,
   INTERNAL = 13,
   UNAVAILABLE = 14,
   DATA_LOSS = 15,
   UNAUTHENTICATED = 16,
}

export enum MessageTypeValues {
   SENT = "SENT",
   RECEIVED = "RECEIVED",
}
